// Data model + merge helpers for the DB-backed References CMS.
//
// A reference powers both a "Selected Work" showcase card and (optionally) a
// full /references/:slug case-study page. English lives in the `en` JSONB column
// on the `references` table; DE/EL live in `reference_translations.data`. The
// merge helpers below overlay a locale's translation on top of the English base
// with per-field fallback, and always force media URLs + icon keys back to the
// English source (translations only carry text).

export type CaseFact = { label: string; value: string };
export type CaseStat = { value: string; label: string };
export type CaseCapability = { icon: string; title: string; body: string };
export type CaseScreenshot = { src: string; alt: string };

export interface ReferenceContent {
  // ---- Card (always used) ----
  /** Short mono tag shown as "/eyebrow" on the card (no leading slash). */
  eyebrow: string;
  /** Short card name, e.g. "Influence Suite". */
  title: string;
  /** Card description paragraph. */
  description: string;

  // ---- Case-study detail (used when has_case_study) ----
  client: string;
  /** Long hero headline; wrap the gradient phrase in [[double brackets]]. */
  headline: string;
  subtitle: string;
  metaTitle: string;
  metaDescription: string;

  facts: CaseFact[];
  stats: CaseStat[];

  challengeKicker: string;
  challengeTitle: string;
  challengeBody: string;
  challengePoints: string[];

  solutionKicker: string;
  solutionTitle: string;
  solutionBody: string;

  capabilitiesKicker: string;
  capabilitiesTitle: string;
  capabilities: CaseCapability[];

  galleryKicker: string;
  galleryTitle: string;
  screenshots: CaseScreenshot[];

  resultKicker: string;
  resultTitle: string;
  resultBody: string;
  resultPoints: string[];

  stackTitle: string;
  stack: string[];

  ctaTitle: string;
  ctaBody: string;
}

export interface ReferenceRow {
  id: string;
  slug: string;
  sort_order: number;
  featured: boolean;
  published: boolean;
  has_case_study: boolean;
  icon: string;
  logo_url: string;
  logo_invert: boolean;
  tags: string[];
  en: ReferenceContent;
}

/** A reference with its content already merged for one language. */
export interface PublicReference {
  id: string;
  slug: string;
  sort_order: number;
  featured: boolean;
  has_case_study: boolean;
  icon: string;
  logo_url: string;
  logo_invert: boolean;
  tags: string[];
  content: ReferenceContent;
}

export const EMPTY_REFERENCE_CONTENT: ReferenceContent = {
  eyebrow: "",
  title: "",
  description: "",
  client: "",
  headline: "",
  subtitle: "",
  metaTitle: "",
  metaDescription: "",
  facts: [],
  stats: [],
  challengeKicker: "// the_challenge",
  challengeTitle: "",
  challengeBody: "",
  challengePoints: [],
  solutionKicker: "// the_solution",
  solutionTitle: "",
  solutionBody: "",
  capabilitiesKicker: "// what_it_does",
  capabilitiesTitle: "",
  capabilities: [],
  galleryKicker: "// inside_the_app",
  galleryTitle: "",
  screenshots: [],
  resultKicker: "// the_result",
  resultTitle: "",
  resultBody: "",
  resultPoints: [],
  stackTitle: "Built with",
  stack: [],
  ctaTitle: "",
  ctaBody: "",
};

/** Normalise a raw JSONB blob into a complete ReferenceContent (fills gaps). */
export function normalizeContent(raw: unknown): ReferenceContent {
  const obj = (raw && typeof raw === "object" ? raw : {}) as Partial<ReferenceContent>;
  return { ...EMPTY_REFERENCE_CONTENT, ...obj };
}

function useTranslated<T>(base: T, tr: T | undefined): T {
  if (tr === undefined || tr === null) return base;
  if (typeof tr === "string") return tr.trim() ? tr : base;
  if (Array.isArray(tr)) return tr.length ? tr : base;
  return tr;
}

/**
 * Merge a locale translation (`data`) over the English base. Text fields fall
 * back to English when missing/empty. Media URLs and icon keys always come from
 * the English base so a translation can never rewrite an asset.
 */
export function mergeContent(
  en: ReferenceContent,
  tr: Partial<ReferenceContent> | undefined | null,
): ReferenceContent {
  const base = normalizeContent(en);
  if (!tr) return base;
  const merged = { ...base } as ReferenceContent;
  (Object.keys(base) as (keyof ReferenceContent)[]).forEach((k) => {
    // @ts-expect-error index write across union field types
    merged[k] = useTranslated(base[k], (tr as ReferenceContent)[k]);
  });

  // Force non-translatable pieces back to the English source.
  merged.screenshots = base.screenshots.map((s, i) => ({
    src: s.src,
    alt: merged.screenshots[i]?.alt?.trim() ? merged.screenshots[i].alt : s.alt,
  }));
  merged.capabilities = merged.capabilities.map((c, i) => ({
    ...c,
    icon: base.capabilities[i]?.icon ?? c.icon,
  }));
  return merged;
}
